import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import Accordian from "../../../../components/Accordian";
import TableBox from '../../../../components/TableBox';
import { TableContextProvider, Validation } from "../../../../interfaces/common";
import { Edit as EditInterface } from "../../../../interfaces/invoice.interface";
import TextArea from "../../../../components/TextArea";
import { VALIDATION_FIELD_MAPPING } from "../../../../utils/appConstant";
import Switch from "../../../../components/Switch";
import Dropdown from "../../../../components/Dropdown";
import Input from "../../../../components/Input";
import Checkbox from "../../../../components/Checkbox";
import Modal from "../../../../components/Modal";
import TransactionFormHeader from "../../../transactions/addEditView/components/FormHeader";
import TransactionButtons from "../../../transactions/addEditView/components/Buttons";
import ReservationFormHeader from "../../../reservations/editView/components/FormHeader";
import ReservationDetails from "../../../reservations/editView/components/Details";
import ReservationButtons from "../../../reservations/editView/components/Buttons";
import { useAddEditViewController as useAddEditViewControllerTransaction } from "../../../transactions/addEditView/hooks";
import { useEditViewController as useEditViewControllerReservation } from "../../../reservations/editView/hooks";
import { AuthContext } from "../../../../contexts/AuthContext";
import { PermissionModulesEnum, PermissionActionsEnum } from "../../../../enums";
import { hasResourseAccess } from "../../../../utils/methods";
import Button from "../../../../components/Button";
import { useContext } from "react";

interface DetailsTabProps {
  tableValues: {
    reservation: TableContextProvider;
    transaction: TableContextProvider;
  }
  formData: EditInterface;
  validation: Validation;
  selectAll: {
    reservations: boolean;
    transactions: boolean;
  };
  dropdownSearchData: any;
  allReviewedByOptions: any[];
  reviewedByOptions?: any[];
  transactionOpen: {
    paramId: string;
    mode: "view" | "edit";
  };
  reservationOpen: {
    paramId: string;
    mode: "view" | "edit";
  }
  view: boolean;
  onCloseTransaction: () => void;
  onCloseReservation: () => void;
  onSelectAll: (e: any, type: 'reservations' | 'transactions') => void;
  onDropdownSearchChange: (e: any, name: string) => void;
  onKeyDown: (e: any, name?: string) => void;
  onInputBlur: (e: any, name: string) => void;
  onInputChange: (e: any, name: string) => void;
}

const DetailsTab: React.FC<DetailsTabProps> = ({
  tableValues,
  formData,
  validation,
  selectAll,
  dropdownSearchData,
  allReviewedByOptions,
  reviewedByOptions,
  transactionOpen,
  reservationOpen,
  view,
  onCloseTransaction,
  onCloseReservation,
  onSelectAll,
  onDropdownSearchChange,
  onKeyDown,
  onInputBlur,
  onInputChange,
}) => {
  const { t } = useTranslation();

  const { permissions, isSystemAdmin } = useContext(AuthContext);
  const [confirmationModal, setConfirmationModal] = useState<{ open: boolean; title: string, description: string }>({ open: false, title: '', description: '' });

  const hasVerifyAccess = hasResourseAccess(isSystemAdmin, permissions, { accessibleTo: [{ [PermissionModulesEnum.Invoices]: [PermissionActionsEnum.Verify] }] });

  const [expanded, setExpanded] = useState<{
    reservation: boolean;
    transaction: boolean;
  }>({
    reservation: true,
    transaction: true
  });

  const transactionController = useAddEditViewControllerTransaction({ paramId: transactionOpen.paramId, mode: transactionOpen.mode, onClose: onCloseTransaction, external: true });
  const reservationController = useEditViewControllerReservation({ paramId: reservationOpen.paramId, mode: reservationOpen.mode, onClose: onCloseReservation, external: true });

  const handleCloseConfirmationModal = useCallback(() => {
    setConfirmationModal({ open: false, title: '', description: '' });
  }, []);

  const reservationSummary = (
    <>
      <i className="ri-calendar-todo-line ri-22px me-2"></i>
      <h6 className="mb-0">{t("invoices.editPreviewPayment.pageTexts.form.detailsTab.accordians.reservations")}</h6>
    </>
  );

  const reservationContent = (
    <div className="row g-6">
      <div className="col-md-12 d-flex align-items-center justify-content-between">
        <div className="row g-3 col-md-2 align-items-center ps-5">
          <div className="form-floating form-floating-outline">
            <Checkbox
              variant='primary'
              className='d-checkbox mb-0'
              checked={selectAll.reservations}
              disabled={view}
              label={t("invoices.editPreviewPayment.pageTexts.form.detailsTab.reservations.selectAll")}
              name={VALIDATION_FIELD_MAPPING.actions}
              onChange={(e) => onSelectAll(e, 'reservations')}
            />
          </div>
        </div>
        <div className="row pe-5">
          <div className="d-flex col-md-2 align-items-center ps-5 gap-3">
            <div className="d-flex align-items-center text-nowrap">
              <i className="ri-circle-fill ri-16px me-1 text-success"></i>
              <span>{t("invoices.editPreviewPayment.pageTexts.form.detailsTab.reservations.matched")}</span>
            </div>
            <div className="d-flex align-items-center text-nowrap">
              <i className="ri-circle-fill ri-16px me-1 text-danger"></i>
              <span>{t("invoices.editPreviewPayment.pageTexts.form.detailsTab.reservations.unmatched")}</span>
            </div>
            <div className="d-flex align-items-center text-nowrap">
              <i className="ri-circle-fill ri-16px me-1 text-warning"></i>
              <span>{t("invoices.editPreviewPayment.pageTexts.form.detailsTab.reservations.pending")}</span>
            </div>
          </div>
        </div>

      </div>
      <div className="col-md-12">
        <TableBox tableValues={tableValues.reservation} />
      </div>
    </div>
  )

  const transactionSummary = (
    <>
      <i className="ri-exchange-dollar-line ri-22px me-2"></i>
      <h6 className="mb-0">{t("invoices.editPreviewPayment.pageTexts.form.detailsTab.accordians.transactions")}</h6>
    </>
  );

  const transactionContent = (
    <div className="row g-3 d-flex align-items-center">
      <div className="col-md-12">
        <div className="row g-3 d-flex align-items-center justify-content-between ps-5 pe-5">
          <div className="col-md-2">
            <div className="form-floating form-floating-outline">
              <Checkbox
                variant='primary'
                className='d-checkbox mb-0'
                disabled={view}
                checked={selectAll.transactions}
                label={t("invoices.editPreviewPayment.pageTexts.form.detailsTab.transactions.selectAll")}
                name={VALIDATION_FIELD_MAPPING.actions}
                onChange={(e) => onSelectAll(e, 'transactions')}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="col-md-12">
        <TableBox tableValues={tableValues.transaction} />
      </div>
    </div>
  )

  const onAccordianChange = useCallback((entity: 'transaction' | 'reservation') => {
    setExpanded(prev => ({
      ...prev,
      [entity]: !prev[entity]
    }));
  }, []);

  return (
    <div className="card-body pt-5">
      {/* <div className="mb-2 d-flex justify-content-end align-items-center">
        <div className="bg-danger legend me-1"></div>
        <span>{t("invoices.editPreviewPayment.pageTexts.form.detailsTab.reconciled")}</span>
      </div> */}

      <Accordian
        expanded={expanded.reservation}
        summary={reservationSummary}
        content={reservationContent}
        bodyClassName="pb-0 px-0"
        onChange={() => onAccordianChange('reservation')}
      />

      <Accordian
        expanded={expanded.transaction}
        summary={transactionSummary}
        content={transactionContent}
        bodyClassName="pb-0 px-0"
        onChange={() => onAccordianChange('transaction')}
      />

      <hr className="my-6 mx-n4" />

      <div className="row g-6">
        <h6 className="mb-0">
          {t('invoices.editPreviewPayment.pageTexts.form.detailsTab.accordians.otherDetails')}
        </h6>
        <div className="form-floating form-floating-outline mt-6">
          <Switch
            label={t('invoices.editPreviewPayment.pageTexts.form.detailsTab.otherDetails.includeNotes.label')}
            name={VALIDATION_FIELD_MAPPING.includeNotes}
            checked={!!formData.includeNotes}
            disabled={view}
            onChange={onInputChange}
          />
        </div>

        <div className="col-md-7">
          <div className="form-floating form-floating-outline">
            <TextArea
              id={VALIDATION_FIELD_MAPPING.notes}
              name={VALIDATION_FIELD_MAPPING.notes}
              label={t('invoices.editPreviewPayment.pageTexts.form.detailsTab.otherDetails.notes.label')}
              placeholder={t('invoices.editPreviewPayment.pageTexts.form.detailsTab.otherDetails.notes.placeholder')}
              value={formData.notes}
              rows={4}
              disabled={view}
              className="form-control-lg h-100"
              required={!formData.reviewed || !!formData.includeNotes}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.notes)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.notes)?.message ?? ''}
              onKeyDown={onKeyDown}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.notes)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.notes)}
            />
          </div>
        </div>

        <div className="col-md-2">
          <div className="form-floating form-floating-outline mb-6">
            <Input
              type="date"
              id={VALIDATION_FIELD_MAPPING.invoiceDate}
              name={VALIDATION_FIELD_MAPPING.invoiceDate}
              disabled={view}
              label={t('invoices.editPreviewPayment.pageTexts.form.invoiceDate.label')}
              placeholder={t('reservations.editPreviewPayment.pageTexts.form.invoiceDate.placeholder')}
              value={formData.invoiceDate}
              onBlur={onInputBlur}
              onChange={onInputChange}
            />
          </div>

          <div className="form-floating form-floating-outline">
            <Input
              type="date"
              id={VALIDATION_FIELD_MAPPING.dueDate}
              name={VALIDATION_FIELD_MAPPING.dueDate}
              disabled={view}
              label={t('invoices.editPreviewPayment.pageTexts.form.dueDate.label')}
              placeholder={t('reservations.editPreviewPayment.pageTexts.form.dueDate.placeholder')}
              value={formData.dueDate}
              onBlur={onInputBlur}
              onChange={onInputChange}
            />
          </div>
        </div>

        <div className="col-md-3">
          <div className="d-flex align-items-center gap-4">
            <div className="form-floating form-floating-outline mt-6 mb-6">
              <Switch
                label={t('invoices.editPreviewPayment.pageTexts.form.detailsTab.otherDetails.reviewed.label')}
                name={VALIDATION_FIELD_MAPPING.reviewed}
                checked={!!formData.reviewed}
                disabled={view || !!formData.isVerified}
                onChange={onInputChange}
              />
            </div>

            {hasVerifyAccess && formData.reviewed && (
              <div className="form-floating form-floating-outline mt-6 mb-6">
                <Switch
                  label={t('invoices.editPreviewPayment.pageTexts.form.detailsTab.otherDetails.verified.label')}
                  name={VALIDATION_FIELD_MAPPING.isVerified}
                  checked={!!formData.isVerified}
                  disabled={view}
                  onChange={(e) => {
                    if (!formData.isVerified && e.target.checked) {
                      setConfirmationModal({ open: true, title: t('invoices.editPreviewPayment.pageTexts.form.detailsTab.otherDetails.verified.confirmationTitle'), description: t('invoices.editPreviewPayment.pageTexts.form.detailsTab.otherDetails.verified.confirmationDescription') });
                    } else {
                      onInputChange(e, VALIDATION_FIELD_MAPPING.isVerified);
                    }
                  }}
                />
              </div>
            )}
          </div>

          <div className="form-floating form-floating-outline">
            <Dropdown
              values={formData.reviewedBy ? [formData.reviewedBy] : []}
              required={!formData.reviewed}
              options={(reviewedByOptions || []).map((item: any) => ({
                label: item.name,
                value: item.id
              }))}
              allOptions={(allReviewedByOptions || []).map((item: any) => ({
                label: item.name,
                value: item.id
              }))}
              id={VALIDATION_FIELD_MAPPING.reviewedBy}
              name={VALIDATION_FIELD_MAPPING.reviewedBy}
              searchText={dropdownSearchData.reviewedBy}
              disabled={view}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.reviewedBy)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.reviewedBy)?.message ?? ''}
              label={t('invoices.editPreviewPayment.pageTexts.form.detailsTab.otherDetails.reviewedBy.label')}
              placeholder={t('invoices.editPreviewPayment.pageTexts.form.detailsTab.otherDetails.reviewedBy.placeholder')}
              onChange={onInputChange}
              onBlur={onInputBlur}
              onSearch={onDropdownSearchChange}
            />
          </div>
        </div>
      </div>



      <Modal
        open={!!transactionOpen.paramId}
        onClose={onCloseTransaction}
        actionClassName="justify-content-start"
        disableBackDrop
        disableEscape
        title={
          <TransactionFormHeader
            external
            formData={transactionController.formData}
            id={transactionController.id || ''}
            view={transactionController.view}
          />
        }
        content={transactionController.formContent}
        action={
          <TransactionButtons
            external
            loading={transactionController.loading}
            actionTwoLoading={transactionController.actionTwoLoading}
            view={transactionController.view}
            edit={transactionController.edit}
            onSubmit={transactionController.handleSubmit}
            onCancel={transactionController.handleCancel}
          />
        }
        maxWidth="xl"
      />

      <Modal
        open={!!reservationOpen.paramId}
        onClose={onCloseReservation}
        actionClassName="justify-content-start"
        disableBackDrop
        disableEscape
        title={
          <ReservationFormHeader
            external
            formData={reservationController.formData}
            view={reservationController.view}
          />
        }
        content={
          <>
            <ReservationDetails loading={reservationController.getContentLoading} formData={reservationController.formData} />
            {reservationController.content}
          </>
        }
        action={
          <ReservationButtons
            external
            loading={reservationController.loading}
            view={reservationController.view}
            onSubmit={reservationController.handleSubmit}
            onCancel={reservationController.handleCancel}
          />
        }
        maxWidth="xl"
      />
      <Modal
        open={confirmationModal.open}
        title={confirmationModal.title}
        titleClassName='text-center'
        actionClassName='justify-content-center'
        content={confirmationModal.description}
        contentClassName="text-center"
        maxWidth="sm"
        onClose={handleCloseConfirmationModal}
        action={
          <div className='d-flex gap-3'>
            <Button
              className="btn btn-outline-secondary"
              text={t("common.buttons.cancel")}
              onClick={handleCloseConfirmationModal}
            />
            <Button
              className="btn btn-primary"
              text={t("common.yes")}
              onClick={() => {
                onInputChange({ target: { checked: true, value: true } }, VALIDATION_FIELD_MAPPING.isVerified);
                handleCloseConfirmationModal();
              }}
            />
          </div>
        }
      />
    </div>
  );
};

export default DetailsTab;